/**
 * HUD distance milestones (backlog): a quiet toast when the journey
 * crosses a distance threshold. Thresholds are deterministic from
 * distance alone (pure `milestoneFor`, headless-tested); the toast is a
 * self-owned DOM element so endless sessions can dispose cleanly.
 *
 * Race place readout (leaderboard, user spec): at MOST `#3/128` — the
 * hero's place out of all racers. The counting is pure (`racePlace`,
 * headless-tested); `PlaceHud` owns its element like the toast does.
 *
 * Surface styling matches the shared playback dock.
 */

/** A quiet translucent surface that leaves the track visible. */
function applyFrame(el: HTMLElement): void {
  Object.assign(el.style, {
    background: "rgba(13, 28, 41, 0.84)",
    border: "1px solid rgba(193, 225, 224, 0.2)",
    borderRadius: "10px",
    backdropFilter: "blur(12px)",
  });
}

/** Milestone thresholds: landmarks early, then every 500 units. */
export function milestoneFor(prevS: number, s: number): number | null {
  if (s <= prevS) return null;
  const fixed = [100, 250, 500, 750, 1000];
  let best: number | null = null;
  for (const m of fixed) {
    if (prevS < m && m <= s) best = m;
  }
  // After 1000: every 500.
  const from = Math.max(1000, prevS);
  const step = Math.floor(from / 500) * 500 + 500;
  for (let m = step; m <= s; m += 500) {
    if (prevS < m) best = m;
  }
  return best;
}

const SHOW_TIME = 2.6;
const FADE = 0.45;

export class MilestoneToast {
  private el: HTMLDivElement;
  private clock = 0;
  private visible = false;
  /** Last text shown (CUA/tests). */
  last = "";

  constructor() {
    this.el = document.createElement("div");
    this.el.id = "milestone";
    this.el.dataset.hud = "";
    Object.assign(this.el.style, {
      position: "fixed",
      top: "88px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: "10",
      fontFamily: "inherit",
      fontSize: "13px",
      fontWeight: "600",
      lineHeight: "1.5",
      whiteSpace: "pre-line",
      textAlign: "center",
      color: "#dce6f5",
      padding: "10px 16px",
      opacity: "0",
      transition: `opacity ${FADE}s ease`,
      pointerEvents: "none",
      backdropFilter: "blur(6px)",
    });
    applyFrame(this.el);
    document.body.appendChild(this.el);
  }

  show(text: string): void {
    this.last = text;
    this.el.textContent = text;
    this.el.style.opacity = "1";
    this.visible = true;
    this.clock = 0;
  }

  update(dt: number): void {
    if (!this.visible) return;
    this.clock += dt;
    if (this.clock >= SHOW_TIME) {
      this.el.style.opacity = "0";
      this.visible = false;
    }
  }

  dispose(): void {
    this.el.remove();
  }
}

/** Hero's race place: 1 + racers strictly ahead of it. Pass every
 * racer's progress — the hero's own s is never strictly ahead of
 * itself, so including it is harmless. Ties share the better place. */
export function racePlace(heroS: number, all: Iterable<number>): number {
  let place = 1;
  for (const s of all) if (s > heroS) place++;
  return place;
}

/** Minimal leaderboard (user spec): a fixed `#3/128` readout, nothing
 * more. Hidden until a real race exists; the session owns the element
 * and disposes it with the run. */
export class PlaceHud {
  private el: HTMLDivElement;
  /** Last text shown (CUA/tests); "" while hidden. */
  last = "";

  constructor() {
    this.el = document.createElement("div");
    this.el.id = "place";
    this.el.dataset.hud = "";
    Object.assign(this.el.style, {
      position: "fixed",
      right: "24px",
      top: "76px",
      zIndex: "10",
      font: "600 14px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace",
      letterSpacing: "0.04em",
      color: "#dce6f5",
      padding: "8px 12px",
      opacity: "0",
      transition: "opacity 0.3s ease",
      pointerEvents: "none",
    });
    applyFrame(this.el);
    document.body.appendChild(this.el);
  }

  /** Show `#{place}/{n}` (only touches the DOM when the text changes).
   * `rivalPlace` (M20b, flag-gated by the caller) appends the hero's
   * grudge target's place as one quiet token: `#3/128 · R#7`. */
  set(place: number, n: number, rivalPlace?: number): void {
    const text = rivalPlace !== undefined
      ? `#${place}/${n} · R#${rivalPlace}`
      : `#${place}/${n}`;
    if (text !== this.last) {
      this.last = text;
      this.el.textContent = text;
    }
    this.el.style.opacity = "1";
  }

  hide(): void {
    if (this.last === "") return;
    this.last = "";
    this.el.style.opacity = "0";
  }

  dispose(): void {
    this.el.remove();
  }
}
